import { useEffect, useState } from 'react';
import { subYears, format, addYears } from 'date-fns';
import { object, string } from 'yup';
import axios from 'axios';

import FormContext from './ContextForm';

import Swal from 'sweetalert2';

import useCart from 'hook/useCart';

import form from 'utils/formUtil';

import { useVerifyPolicy } from 'api/postPolicyVerify';
import LayoutForm from 'page/insurance-form/LayoutForm';
import usePkg from 'hook/usePkg';
import ConfirmationPage from './ConfirmationPage';
import DashboardLayout from 'layout/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'component/Navbars/DashboardNavbar';
import { useUserAddress } from 'api/user/useUserAddress';

const removeWhiteSpace = (str) => {
  return str.replace(/(\s|,|\.)/g, '');
};

const employeeDefaultScheme = object({
  prefix: form.schema.prefix,
  name: string().required(),
  bd: string().min(3).required(),
  nationality: form.schema.nationality,
  idCard: string().min(3).required(),
  cardType: form.schema.idCardType,
  policyDate: string().nullable(),
});

const employeeScheme = employeeDefaultScheme.shape({
  employer: string().required(),
  employerId: string().nullable(),
  address1: string().required(),
  district: string().required(),
  area: string().required(),
  province: string().required(),
  zipcode: string().required(),
});

const currDate = new Date();
const initialDate = subYears(currDate, 18);

const defaultAddress = [{ value: 'default', label: 'ใช้ที่อยู่ตามที่สมัคร' }];

function InsuranceForm() {
  const { options, priceList } = usePkg();
  const { mutateAsync: verifyOrder } = useVerifyPolicy();
  const { addItems } = useCart();

  const [pageIdx, setPageIdx] = useState(0);
  const [disabled, setDisabled] = useState(true);

  const [prefix, setPrefix] = useState();
  const [name, setName] = useState('');
  const [bd, setBd] = useState(initialDate);
  const [nationality, setNationality] = useState();
  const [idCard, setIdCard] = useState('');
  const [cardType, setCardType] = useState();
  const [policyDate, setPolicyDate] = useState(currDate);
  const [toggle, setToggle] = useState(true);
  const [employer, setEmployer] = useState('');
  const [employerId, setEmployerId] = useState('');
  const [address1, setAddress1] = useState('');
  const [district, setDistrict] = useState('');
  const [area, setArea] = useState('');
  const [province, setProvince] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [pkg, setPkg] = useState(options[1]);

  const [addresses, setAddresses] = useState(defaultAddress);
  const [address, setAddress] = useState(defaultAddress[0]);

  const [open, setOpen] = useState(false);

  const formData = {
    prefix,
    setPrefix,
    name,
    setName,
    bd,
    setBd,
    nationality,
    setNationality,
    idCard,
    setIdCard,
    cardType,
    setCardType,
    policyDate,
    setPolicyDate,
    toggle,
    setToggle,
    employer,
    setEmployer,
    employerId,
    setEmployerId,
    address1,
    setAddress1,
    district,
    setDistrict,
    area,
    setArea,
    province,
    setProvince,
    zipcode,
    setZipcode,
    pkg,
    setPkg,

    addresses,
    setAddresses,
    address,
    setAddress,
  };

  const { data, isFetching, refetch: refreshAddress } = useUserAddress('1234');

  useEffect(() => {
    if (!data) {
      return;
    }
    if (data.length) {
      const craftAddr = data.map((x) => {
        const { _id, address1, district, area } = x;
        const str = `${address1} ${district} ${area}...`;
        return {
          value: _id,
          label: str,
        };
      });
      setAddresses([defaultAddress[0], ...craftAddr]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  useEffect(() => {
    if (policyDate) {
      // console.log(policyDate);
      const nextYear = addYears(currDate, 1);
      if (new Date(policyDate).getFullYear() > nextYear.getFullYear()) {
        const selDt = new Date(policyDate);
        const dd = format(selDt, 'dd');
        const MM = format(selDt, 'MM');
        setPolicyDate(new Date(`${currDate.getFullYear()}-${MM}-${dd}`));
      }
    }
  }, [policyDate]);

  useEffect(() => {
    function handleDisabled() {
      if (!disabled) {
        setDisabled(true);
      }
    }
    function handleEnabled() {
      if (disabled) {
        setDisabled(false);
      }
    }

    const data = {
      prefix: prefix?.value,
      name,
      bd,
      nationality: nationality?.value,
      idCard,
      cardType: cardType?.value,
      policyDate,
      pkg: pkg?.value,
    };
    if (toggle) {
      employeeDefaultScheme
        .validate(data)
        .then(() => {
          handleEnabled();
        })
        .catch(() => {
          handleDisabled();
        });
    } else {
      // NOTE:  when toggle off validate address also
      employeeScheme
        .validate({ ...data, employer, employerId, address1, district, area, province, zipcode })
        .then(handleEnabled)
        .catch(handleDisabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prefix?.value,
    name,
    bd,
    nationality?.value,
    idCard,
    cardType?.value,
    policyDate,
    toggle,
    employer,
    employerId,
    address1,
    district,
    area,
    province,
    zipcode,
    pkg?.value,
  ]);

  function resetState() {
    setPrefix();
    setName('');
    setBd(initialDate);
    setNationality();
    setIdCard('');
    setCardType();
    setPolicyDate(currDate);
    setEmployer('');
    setEmployerId('');
    setAddress1('');
    setDistrict('');
    setArea('');
    setProvince('');
    setZipcode('');
    setPkg(options[0]);
  }
  function resetPrimary() {
    setPrefix();
    setName('');
    setBd(initialDate);
    setNationality();
    setIdCard('');
    setCardType();
  }

  const handleSubmit = () => {
    const data = {
      prefix: prefix?.value,
      name,
      bd: format(bd, 'dd/MM/yyyy'),
      nationality: nationality?.value,
      idCard: removeWhiteSpace(idCard),
      cardType: cardType?.value,
      policyDate: format(policyDate, 'dd/MM/yyyy'),
      address: address.value,
      // employer,
      // employerId,
      // address1,
      // district,
      // area,
      // province,
      // zipcode,
      status: 'create',
      doctype: 'form',
      pkg: pkg?.value,
    };

    const confirmSwal = Swal.mixin({
      customClass: {
        confirmButton: 'button button-success',
        cancelButton: 'button button-error',
      },
      buttonsStyling: false,
    });

    confirmSwal
      .fire({
        title: 'ยืนยันการซื้อ?',
        text: 'กรมธรรม์จะถูกเพิ่มไปที่รถเข็นเมื่อทำการยืนยัน',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      })
      .then((result) => {
        if (result.isConfirmed) {
          verifyPopup(data)
            .then((res) => {
              // NOTE: add to cart
              addItems(res);
              resetPrimary();
              setPageIdx(0);
              Swal.fire('สำเร็จ', 'เพิ่มไปที่รถเช็นแล้ว', 'success');
            })
            .catch((err) => {
              if (axios.isAxiosError(err)) {
                console.log(err.response);
                Swal.fire('ผิดพลาด', err.response.data, 'error');
              } else {
                Swal.fire('ผิดพลาด', err?.message, 'error');
              }
            });
        }
      });
  };

  async function verifyPopup(data) {
    Swal.fire({
      title: 'กำลังดำนเนิการ',
      html: 'กรุณารอสักครู่...',
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    return await verifyOrder([data])
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => {
        Swal.close();
      });
  }

  async function firstValidate() {
    try {
      const data = {
        prefix: prefix?.value,
        name,
        bd: format(bd, 'dd/MM/yyyy'),
        nationality: nationality?.value,
        idCard: removeWhiteSpace(idCard),
        cardType: cardType?.value,
        policyDate: format(policyDate, 'dd/MM/yyyy'),
        address: address.value,
        status: 'create',
        doctype: 'form',
        pkg: pkg?.value,
      };

      Swal.fire({
        title: 'กำลังดำเนินการ',
        html: 'โปรดรอซักครู่...',
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await verifyPopup(data);

      Swal.close();
      return setPageIdx(1);
    } catch (err) {
      console.log(err);
      if (axios.isAxiosError(err)) {
        // console.log(err.response);
        Swal.fire({
          title: 'กรุณาตรวจสอบ',
          icon: 'error',
          html: `${err.response.data}`,
          showConfirmButton: false,
          showCancelButton: true,
          cancelButtonText: 'แก้ไข',
          didOpen: () => {
            Swal.hideLoading();
          },
        });
      } else {
      Swal.fire({
        title: 'กรุณาตรวจสอบ',
        icon: 'error',
        html: `${err?.message}`,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: 'แก้ไข',
        didOpen: () => {
          Swal.hideLoading();
        },
      });
    }
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <FormContext.Provider
        value={{
          pageIdx,
          setPageIdx,
          priceList,
          disabled,
          setDisabled,
          handleSubmit,
          formData,
          resetState,

          refreshAddress,
          open,
          setOpen,
          firstValidate,
        }}
      >
        {pageIdx === 0 ? <LayoutForm /> : <ConfirmationPage />}
      </FormContext.Provider>
    </DashboardLayout>
  );
}

export default InsuranceForm;
